import { memo, useCallback, useMemo } from 'react';

import { type Board } from '@air/types';

import { DragType, type GetBoardDragItem, type RearrangableItem } from '~/components/Shared/Drag/dragTypes';
import { DNDRearrangeArea } from '~/components/v2/components/shared/dnd/DNDRearrangeArea';
import { useIsScrollElementVisible } from '~/components/v2/components/shared/scroll/useIsScrollElementVisible';
import {
  WorkspaceSidebarBoardsListItem,
  type WorkspaceSidebarBoardsListItemProps,
} from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItem';
import {
  WorkspaceSidebarVirtualizedList,
  type WorkspaceSidebarVirtualizedListProps,
} from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarVirtualizedList';
import { useBoardsBeingDeleted } from '~/swr-hooks/boards/useBoardsBeingDeleted';

const DRAG_TYPES = [
  DragType.boardNavFavorites,
  DragType.boardNavWorkspace,
  DragType.boardNavLibrary,
  DragType.boardCard,
];

export type WorkspaceSidebarBoardsListProps = Pick<
  WorkspaceSidebarBoardsListItemProps,
  'draggable' | 'droppable' | 'dragType' | 'supportedBoardDragTypes'
> &
  Pick<Partial<WorkspaceSidebarVirtualizedListProps<Board>>, 'scrollElement'> & {
    boards: Board[];
    depth: number;
    getCanRearrange: (props: { parentBoardId: Board['id'] | null; board: GetBoardDragItem }) => boolean;
    onBoardRearrange: (props: {
      adjacentItem: RearrangableItem;
      parentBoardId: Board['id'] | null;
      boards: Board[];
      board: GetBoardDragItem;
    }) => void;
    parentBoardId: Board['id'] | null;
  };

export const WorkspaceSidebarBoardsList = memo(
  ({
    boards,
    depth,
    dragType,
    draggable,
    droppable,
    getCanRearrange,
    onBoardRearrange,
    parentBoardId,
    supportedBoardDragTypes,
    scrollElement,
  }: WorkspaceSidebarBoardsListProps) => {
    const isScrollElementVisible = useIsScrollElementVisible(scrollElement ?? null);
    const boardsBeingDeleted = useBoardsBeingDeleted();

    const filteredBoards = useMemo(() => {
      return !boardsBeingDeleted || boardsBeingDeleted.length === 0
        ? boards
        : boards.filter((board) => !boardsBeingDeleted?.includes(board.id));
    }, [boards, boardsBeingDeleted]);

    const _canRearrangeBoard = useCallback(
      (index: number) => (draggedBoard: GetBoardDragItem) => {
        /**
         * If the board the user is trying to move is an ancestor
         * of any of the boards on this level (they're trying to move the board into itself),
         * prevent them from doing it
         */
        const isAncestorOfAnyBoard = boards.find(({ ancestors = [] }) =>
          ancestors.find(({ id }) => id === draggedBoard.id),
        );

        if (isAncestorOfAnyBoard) {
          return false;
        }

        /**
         * If they are trying to move a board to the position it's already in, don't let them.
         *
         * For example, if they're moving Board 1...
         *
         * ...... <-- Don't allow Board 1 to be moved here
         * Board 1
         * ...... <-- or here (because this is the same position)
         * Board 2
         * ......
         * Board 3
         * ......
         */

        if (
          /**
           * if it's the same parent board (or both the root of the workspace/library)
           */
          draggedBoard.parentId === parentBoardId &&
          /** and the index is it's current position */
          (draggedBoard.index === index || draggedBoard.index === index + 1) &&
          /**
           * and it's the same library, prevent it.
           *
           * We can't just check the index because it could be the same index but of a different library
           * Just get the libraryId of the first board in the tree, since all boards in the tree will be in the same library
           * */
          draggedBoard.firstItem.library?.id === boards[0]?.library?.id
        ) {
          return false;
        }

        return getCanRearrange({ parentBoardId, board: draggedBoard });
      },
      [boards, getCanRearrange, parentBoardId],
    );

    const onItemRearrange = useCallback(
      ({ draggedBoard, index }: { draggedBoard: GetBoardDragItem; index: number }) => {
        onBoardRearrange({
          adjacentItem: {
            direction: 'after',
            index,
          },
          boards,
          parentBoardId,
          board: draggedBoard,
        });
      },
      [onBoardRearrange, boards, parentBoardId],
    );

    const renderItem = useCallback(
      ({ index, item }: { index: number; item: Board }) => {
        return (
          <div className="relative" key={item.id}>
            {index === 0 && droppable && (
              <DNDRearrangeArea
                canDropItem={_canRearrangeBoard(-1)}
                dragTypes={DRAG_TYPES}
                dropLocation="sub-nav-rearrange"
                onItemRearrange={(draggedBoard) => onItemRearrange({ draggedBoard, index: -1 })}
                side="top"
              />
            )}
            <WorkspaceSidebarBoardsListItem
              board={item}
              depth={depth}
              dragType={dragType}
              draggable={draggable}
              droppable={droppable}
              onBoardRearrange={onBoardRearrange}
              index={index}
              supportedBoardDragTypes={supportedBoardDragTypes}
            />
            {droppable && (
              <DNDRearrangeArea
                canDropItem={_canRearrangeBoard(index)}
                dragTypes={DRAG_TYPES}
                dropLocation="sub-nav-rearrange"
                onItemRearrange={(draggedBoard) => onItemRearrange({ draggedBoard, index: index })}
                side="bottom"
              />
            )}
          </div>
        );
      },
      [
        _canRearrangeBoard,
        depth,
        dragType,
        draggable,
        droppable,
        onBoardRearrange,
        onItemRearrange,
        supportedBoardDragTypes,
      ],
    );

    return (
      <>
        {scrollElement && isScrollElementVisible ? (
          <WorkspaceSidebarVirtualizedList
            items={filteredBoards}
            renderItem={renderItem}
            scrollElement={scrollElement}
          />
        ) : (
          <div className="pb-1">{filteredBoards.map((board, index) => renderItem({ index, item: board }))}</div>
        )}
      </>
    );
  },
);

WorkspaceSidebarBoardsList.displayName = 'WorkspaceSidebarBoardsList';
